import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
  memo,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// PURE HELPERS — module-scope
// ---------------------------------------------------------------------------

const isoToFlagEmoji = (iso) => {
  if (!iso || iso.length !== 2) return "";
  const codePoints = [...iso.toUpperCase()].map(
    (c) => 127397 + c.charCodeAt(0),
  );
  return String.fromCodePoint(...codePoints);
};

/**
 * Extracts the dial code from any country object shape your API might return.
 * Always returns a string starting with "+" or empty string.
 */
const getDialCode = (c) => {
  if (!c) return "";
  const raw =
    c.code ??
    c.dialCode ??
    c.phoneCode ??
    c.callingCode ??
    c.countryCallingCode ??
    "";
  const s = String(raw).trim();
  if (!s) return "";
  return s.startsWith("+") ? s : `+${s}`;
};

/**
 * Extracts the 2-letter ISO code from any country object shape.
 */
const getIso2 = (c) => {
  if (!c) return "";
  return (c.isoCode ?? c.iso2 ?? c.countryCode ?? c.sortname ?? c.code2 ?? "")
    .toString()
    .trim()
    .toUpperCase();
};

/**
 * Gets the flag emoji from the country object, falling back to ISO conversion.
 */
const getFlag = (c) => {
  if (!c) return "";
  return c.flag || c.flagEmoji || c.emoji || isoToFlagEmoji(getIso2(c));
};

const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
};

// ---------------------------------------------------------------------------
// OTP BOXES — memoised, keyboard-navigable
// ---------------------------------------------------------------------------

const OtpBoxes = memo(function OtpBoxes({
  length = 4,
  value,
  onChange,
  disabled,
}) {
  const inputsRef = useRef([]);

  const digits = useMemo(() => {
    const v = String(value || "");
    return Array.from({ length }, (_, i) => v[i] || "");
  }, [value, length]);

  const focus = useCallback((i) => inputsRef.current[i]?.focus(), []);

  const setAt = useCallback(
    (i, char) => {
      const next = digits.slice();
      next[i] = char;
      onChange(next.join(""));
    },
    [digits, onChange],
  );

  const handleChange = useCallback(
    (i, e) => {
      const raw = e.target.value || "";
      const onlyDigits = raw.replace(/\D/g, "");

      if (onlyDigits.length > 1) {
        const merged = (digits.join("") + onlyDigits)
          .replace(/\D/g, "")
          .slice(0, length);
        onChange(merged);
        focus(Math.min(merged.length, length - 1));
        return;
      }

      const d = onlyDigits.slice(-1);
      setAt(i, d);
      if (d && i < length - 1) focus(i + 1);
    },
    [digits, length, onChange, focus, setAt],
  );

  const handleKeyDown = useCallback(
    (i, e) => {
      if (e.key === "Backspace") {
        if (digits[i]) {
          setAt(i, "");
        } else if (i > 0) {
          focus(i - 1);
          setAt(i - 1, "");
        }
      }
      if (e.key === "ArrowLeft" && i > 0) focus(i - 1);
      if (e.key === "ArrowRight" && i < length - 1) focus(i + 1);
    },
    [digits, length, focus, setAt],
  );

  const handlePaste = useCallback(
    (e) => {
      const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "");
      if (!text) return;
      e.preventDefault();
      onChange(text.slice(0, length));
      focus(Math.min(text.length, length - 1));
    },
    [length, onChange, focus],
  );

  return (
    <div className="flex gap-3" onPaste={handlePaste}>
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => (inputsRef.current[i] = el)}
          value={digits[i]}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          inputMode="numeric"
          maxLength={1}
          disabled={disabled}
          className="w-12 h-12 text-center text-lg bg-white/[0.04] border border-white/[0.1] rounded-lg focus:border-[#C6A87C] focus:outline-none transition-colors disabled:opacity-60"
          aria-label={`OTP digit ${i + 1}`}
          data-testid={`login-otp-box-${i}`}
        />
      ))}
    </div>
  );
});

// ---------------------------------------------------------------------------
// CUSTOM COUNTRY CODE SELECTOR — transparent, shows flag + code
// ---------------------------------------------------------------------------

const CountryCodeSelect = memo(function CountryCodeSelect({
  countries,
  value,
  onChange,
  disabled,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  // Find the currently selected country object
  const selectedCountry = useMemo(() => {
    if (!countries.length) return null;
    return countries.find((c) => getDialCode(c) === value) || null;
  }, [countries, value]);

  const selectedFlag = selectedCountry ? getFlag(selectedCountry) : "";
  const selectedName = selectedCountry?.name || "";

  // Filtered list
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        getDialCode(c).includes(q) ||
        getIso2(c).toLowerCase().includes(q),
    );
  }, [countries, search]);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen]);

  // Auto-focus search when opening
  useEffect(() => {
    if (isOpen) {
      const t = setTimeout(() => searchRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (!isOpen || highlightIndex < 0) return;
    const listEl = listRef.current;
    if (!listEl) return;
    const item = listEl.children[highlightIndex];
    if (item) item.scrollIntoView({ block: "nearest" });
  }, [isOpen, highlightIndex]);

  // Reset highlight when search changes
  useEffect(() => {
    setHighlightIndex(filtered.length > 0 ? 0 : -1);
  }, [search, filtered.length]);

  const toggleOpen = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => {
      if (!prev) {
        const idx = countries.findIndex((c) => getDialCode(c) === value);
        setHighlightIndex(idx >= 0 ? idx : 0);
        setSearch("");
      }
      return !prev;
    });
  }, [disabled, countries, value]);

  const selectItem = useCallback(
    (dialCode) => {
      onChange(dialCode);
      setIsOpen(false);
      setSearch("");
    },
    [onChange],
  );

  const handleTriggerKeyDown = useCallback(
    (e) => {
      if (!isOpen) {
        if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
          e.preventDefault();
          toggleOpen();
        }
        return;
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : 0,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightIndex((prev) =>
            prev > 0 ? prev - 1 : filtered.length - 1,
          );
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (highlightIndex >= 0 && highlightIndex < filtered.length) {
            selectItem(getDialCode(filtered[highlightIndex]));
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setSearch("");
          break;
        default:
          break;
      }
    },
    [isOpen, filtered, highlightIndex, toggleOpen, selectItem],
  );

  const handleSearchKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : 0,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightIndex((prev) =>
            prev > 0 ? prev - 1 : filtered.length - 1,
          );
          break;
        case "Enter":
          e.preventDefault();
          if (highlightIndex >= 0 && highlightIndex < filtered.length) {
            selectItem(getDialCode(filtered[highlightIndex]));
          }
          break;
        case "Escape":
          e.preventDefault();
          setIsOpen(false);
          setSearch("");
          break;
        default:
          break;
      }
    },
    [filtered, highlightIndex, selectItem],
  );

  return (
    <div
      ref={containerRef}
      className="relative"
      data-testid="country-code-select-wrapper"
    >
      {/* Trigger button */}
      <button
        type="button"
        onClick={toggleOpen}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Country code: ${selectedName || value}`}
        data-testid="country-code-select"
        className={`
          flex items-center gap-2
          bg-transparent
          border border-white/[0.1]
          rounded-lg
          px-3 py-3
          min-w-[110px]
          text-left
          transition-colors
          focus:outline-none
          focus:border-[#C6A87C]
          disabled:opacity-60 disabled:cursor-not-allowed
          ${isOpen ? "border-[#C6A87C]" : "hover:border-white/20"}
        `}
      >
        {selectedFlag && (
          <span className="text-lg leading-none shrink-0">{selectedFlag}</span>
        )}
        <span className="text-white text-sm font-medium whitespace-nowrap">
          {value || "+1"}
        </span>
        <ChevronDown
          size={14}
          className={`ml-auto text-white/50 shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div
          role="listbox"
          aria-label="Select country code"
          data-testid="country-code-dropdown"
          className="
            absolute left-0 top-[calc(100%+4px)] z-50
            w-[280px]
            rounded-xl
            border border-white/[0.1]
            bg-[#0A1D1A]/95
            backdrop-blur-xl
            shadow-2xl
            overflow-hidden
          "
        >
          {/* Search bar */}
          <div className="p-2 border-b border-white/[0.06]">
            <div className="flex items-center gap-2 bg-white/[0.04] rounded-lg px-3 py-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-white/30 shrink-0"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.3-4.3" />
              </svg>
              <input
                ref={searchRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search country or code…"
                className="
                  bg-transparent text-sm text-white/80
                  placeholder:text-white/30 outline-none flex-1
                "
                aria-label="Search countries"
                data-testid="country-code-search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                  className="text-white/30 hover:text-white/60 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Country list */}
          <ul
            ref={listRef}
            className="max-h-[220px] overflow-y-auto py-1"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(198,168,124,0.3) transparent",
            }}
          >
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-sm text-white/40 text-center">
                No countries found
              </li>
            ) : (
              filtered.map((c, idx) => {
                const dc = getDialCode(c);
                if (!dc) return null;

                const flag = getFlag(c);
                const name = c.name || getIso2(c) || dc;
                const isSelected = dc === value;
                const isHighlighted = idx === highlightIndex;

                return (
                  <li
                    key={c._id || c.id || `${dc}-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    data-testid={`country-option-${getIso2(c) || idx}`}
                    onClick={() => selectItem(dc)}
                    onMouseEnter={() => setHighlightIndex(idx)}
                    className={`
                      flex items-center gap-3
                      px-3 py-2.5
                      cursor-pointer
                      transition-colors
                      ${isHighlighted ? "bg-white/[0.08]" : "hover:bg-white/[0.04]"}
                      ${isSelected ? "text-[#C6A87C]" : "text-white/85"}
                    `}
                  >
                    <span className="text-lg leading-none shrink-0 w-6 text-center">
                      {flag}
                    </span>
                    <span className="text-sm truncate flex-1">{name}</span>
                    <span
                      className={`text-xs font-mono shrink-0 ${
                        isSelected ? "text-[#C6A87C]" : "text-white/50"
                      }`}
                    >
                      {dc}
                    </span>
                    {isSelected && (
                      <Check size={14} className="text-[#C6A87C] shrink-0" />
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// LOGIN PAGE
// ---------------------------------------------------------------------------

export default function Login() {
  const { requestOtp, confirmOtp, guestLogin, getCountryCodes } = useAuth();

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const [countryCodes, setCountryCodes] = useState([]);
  const [dialCode, setDialCode] = useState("+592");

  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");

  const [step, setStep] = useState("PHONE");
  const [testOtp, setTestOtp] = useState(null);

  // Abort ref for country codes fetch
  const abortRef = useRef(null);

  useEffect(() => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    let mounted = true;

    (async () => {
      try {
        const raw = await getCountryCodes?.();
        if (!mounted) return;

        // Handle both { data: [...] } and direct array responses
        const arr = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];

        setCountryCodes(arr);

        // Set default dial code to first country if available
        if (arr.length > 0) {
          const firstCode = getDialCode(arr[0]);
          if (firstCode) setDialCode(firstCode);
        }
      } catch {
        // Keep defaults
      }
    })();

    return () => {
      mounted = false;
      abortRef.current?.abort();
    };
  }, [getCountryCodes]);

  const phoneNumber = useMemo(() => {
    const dc = (dialCode || "").trim();
    const local = (phone || "").trim().replace(/\s+/g, "");
    return `${dc}${local}`;
  }, [dialCode, phone]);

  const handleDialCodeChange = useCallback((code) => {
    setDialCode(code);
  }, []);

  const handlePhoneChange = useCallback((e) => {
    setPhone(e.target.value);
  }, []);

  const handleOtpChange = useCallback((val) => {
    setOtp(val);
  }, []);

  const resetToPhone = useCallback(() => {
    setStep("PHONE");
    setOtp("");
    setErr("");
    setTestOtp(null);
  }, []);

  const submit = useCallback(
    async (e) => {
      e.preventDefault();
      setBusy(true);
      setErr("");

      try {
        if (step === "PHONE") {
          const r = await requestOtp(phoneNumber);
          if (r.ok) {
            const otpFromApi = r?.data?.data?.otp;
            setTestOtp(otpFromApi ?? null);
            setStep("OTP");
            toast.success("OTP sent successfully.");
          } else {
            setErr(r.error);
          }
        } else {
          if (String(otp).length !== 4) {
            setErr("Please enter the 4-digit OTP.");
            return;
          }

          const r = await confirmOtp(phoneNumber, otp);
          if (r.ok) {
            // Check if user needs to complete registration
            const userData = r?.data?.data || r?.data;
            // Store user details
            localStorage.setItem("firstName", userData.firstName || "");
            localStorage.setItem("lastName", userData.lastName || "");
            localStorage.setItem("fullName", userData.name || "");
            localStorage.setItem("Email", userData.email || "");
            if (userData?.requiresRegistration === true) {
              toast.success("OTP verified. No account found! Please complete registration.");
              nav("/register", {
                state: {
                  phoneNumber: userData.phoneNumber || phoneNumber,
                  phoneVerified: true,
                },
              });
            } else {
              toast.success("Welcome back to vmeetio.");
              nav("/");
            }
          } else {
            setErr(r.error);
          }
        }
      } finally {
        setBusy(false);
      }
    },
    [step, phoneNumber, otp, requestOtp, confirmOtp, nav],
  );

  const doGuestLogin = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await guestLogin();
      if (r.ok) {
        toast.success("Welcome to vmeetio.");
        nav("/");
      } else {
        setErr(r.error);
      }
    } finally {
      setBusy(false);
    }
  }, [guestLogin, nav]);

  return (
    <div data-testid="login-page" className="min-h-[80vh] flex items-center">
      <div className="container-x grid md:grid-cols-12 gap-12 py-16">
        {/* Left panel */}
        <div className="md:col-span-6 hidden md:block">
          <p className="overline mb-5">Sign in</p>
          <h1 className="font-display font-light text-5xl lg:text-6xl tracking-tighter leading-[0.95] mb-6">
            Welcome back to
            <br />
            <span className="italic text-[#C6A87C]">the conversation.</span>
          </h1>
          <p className="text-white/65 max-w-md">
            Your circles, your tables, your dossiers — all where you left them.
          </p>
        </div>

        {/* Right panel — form */}
        <div className="md:col-span-5 md:col-start-8">
          <form
            onSubmit={submit}
            className="glass rounded-2xl p-8 space-y-5"
            data-testid="login-form"
          >
            <p className="overline">Member sign in</p>
            <h2 className="font-display text-3xl tracking-tight">Sign in</h2>

            {/* Phone field */}
            <div>
              <label className="overline block mb-2">Phone</label>

              <div className="flex gap-3">
                <CountryCodeSelect
                  countries={countryCodes}
                  value={dialCode}
                  onChange={handleDialCodeChange}
                  disabled={step === "OTP"}
                />

                <input
                  required
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  data-testid="login-phone-input"
                  className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg px-4 py-3 focus:border-[#C6A87C] focus:outline-none transition-colors disabled:opacity-60"
                  placeholder="987XXXXXX"
                  disabled={step === "OTP"}
                />
              </div>

              {step === "OTP" && (
                <button
                  type="button"
                  onClick={resetToPhone}
                  className="mt-2 text-sm text-white/55 hover:text-white transition-colors"
                >
                  ← Change phone number
                </button>
              )}
            </div>

            {/* OTP field */}
            {step === "OTP" && (
              <div>
                <label className="overline block mb-2">OTP</label>

                <OtpBoxes
                  length={4}
                  value={otp}
                  onChange={handleOtpChange}
                  disabled={busy}
                />

                {/* FOR TESTING ONLY */}
                {testOtp != null && (
                  <p className="mt-2 text-sm text-white/65">
                    Test OTP:{" "}
                    <span className="text-[#C6A87C] font-semibold">
                      {testOtp}
                    </span>
                  </p>
                )}
              </div>
            )}

            {/* Error */}
            {err && (
              <p className="text-sm text-red-300" data-testid="login-error">
                {err}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="btn-gold w-full justify-center disabled:opacity-60"
            >
              {busy
                ? step === "PHONE"
                  ? "Sending OTP…"
                  : "Verifying…"
                : step === "PHONE"
                  ? "Send OTP"
                  : "Sign in"}{" "}
              <ArrowRight size={16} />
            </button>

            {/* Guest login */}
            <button
              type="button"
              onClick={doGuestLogin}
              disabled={busy}
              data-testid="guest-login"
              className="w-full bg-white/[0.04] border border-white/[0.1] rounded-lg px-4 py-3 hover:bg-white/[0.06] transition-colors disabled:opacity-60"
            >
              Continue as guest
            </button>

            <p className="text-sm text-white/55 text-center">
              New to vmeetio?{" "}
              <Link
                to="/register"
                className="text-[#C6A87C] hover:underline"
                data-testid="register-link"
              >
                Request access
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
